import { useTranslation } from "react-i18next";

import { locale } from "../locale.js";
import { useFreshness } from "../offline.js";

/**
 * The line that says this is the cellar as it stood, not the cellar.
 *
 * `tech-stack.md` made this a condition of caching anything at all, and the
 * reason is a distinction that matters at the cabinet: for *where is it*,
 * stale is harmless, because bottles do not move themselves. For *how many are
 * left* it is a lie, because somebody else drank one. The interface cannot
 * tell which question is being asked, so it says which cellar it is showing
 * and lets the person holding the phone decide what that is worth.
 *
 * It carries the time rather than the word *gammal*: half past six this
 * evening is something a person can weigh against what they remember doing
 * since, and *may be out of date* is not.
 *
 * Nothing is shown while the connection works, including during the fraction
 * of a second when a stored answer is on screen and the network is confirming
 * it behind — see `Freshness`. A bar that is up on every load is a bar that is
 * read on none of them.
 */
export function CacheNotice() {
  const { t } = useTranslation();
  const freshness = useFreshness();
  if (freshness.status === "live") return null;

  const { storedAt } = freshness;
  const today = storedAt.toDateString() === new Date().toDateString();
  const message = today
    ? t("offline.today", {
        time: storedAt.toLocaleTimeString(locale(), {
          hour: "2-digit",
          minute: "2-digit",
        }),
      })
    : t("offline.earlier", {
        when: storedAt.toLocaleString(locale(), {
          day: "numeric",
          month: "long",
          hour: "2-digit",
          minute: "2-digit",
        }),
      });

  return (
    // `role="status"` rather than an alert: it is worth saying and not worth
    // interrupting for, and a screen reader should reach it in its own time.
    // Its own rule under the header, in the muted voice — the page below is
    // still the thing being read, and a warning painted in a warning colour
    // would be the loudest thing on a screen whose loudest thing is supposed
    // to be the drinking-window band.
    <div
      role="status"
      className="border-line text-muted border-b px-5 py-2 text-center text-sm"
    >
      {message}
    </div>
  );
}
